import { faPaperPlane } from '@fortawesome/pro-light-svg-icons/faPaperPlane';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { IFaq } from '@propertypal/shared/src/types/strapi';
import { agentSchema, publicSchema } from '@propertypal/shared/src/validation/schemas/faqFormSchema';
import { openToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import TextArea from '../inputs/TextArea';
import TextInput from '../inputs/TextInput';
import { getCaptchaKey } from '../security/Recaptcha';
import { SubHeading, Text } from '../typography';
import { FormContainer, FormWrapper, Sent } from './FaqForm.style';

interface FaqFormProps {
  type: Exclude<IFaq['attributes']['form'], 'none' | null>;
  query: IFaq['attributes']['question'];
}

const FaqForm: React.FC<FaqFormProps> = ({ type, query }) => {
  const [sent, setSent] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const dispatch = useDispatch();

  const onSubmit = async (values: any) => {
    setSending(true);

    try {
      const recaptcha = await getCaptchaKey('enquire');
      const body = {
        name: values.name,
        email: values.email,
        telephone: values.number,
        comments: values.comments,
        recaptcha,
        reason: query || 'FAQ',
        customData:
          type === 'agentForm'
            ? {
                companyName: values.companyName,
              }
            : undefined,
      };

      await axios(
        {
          method: 'POST',
          url: `${API_URL}/enquire`,
          data: body,
        },
        dispatch,
      );

      setSent(true);
    } catch {
      openToast({
        title: 'Error',
        message: "Something went wrong. We couldn't send your enquiry. Please try again!",
        type: 'error',
      });
    }

    setSending(false);
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      number: '',
      email: '',
      companyName: '',
      comments: '',
    },
    validationSchema: type === 'publicForm' ? publicSchema : agentSchema,
    onSubmit: (values) => {
      if (!sending) onSubmit(values);
    },
  });

  return (
    <FormWrapper data-testid="faq-form">
      {sent ? (
        <Sent
          id="faq-form"
          data-testid="faq-form-sent"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <FontAwesomeIcon icon={faPaperPlane} style={{ fontSize: 46 }} />
          <SubHeading textTransform="uppercase" mt={20} fontSize={28} fontWeight="600">
            Thanks!
          </SubHeading>
          <Text mt={20} align="center" fontSize={18}>
            Your enquiry has been sent, and a member of the team will be in touch shortly
          </Text>
        </Sent>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <FormContainer>
            <TextInput
              placeholder="Your Name"
              name="name"
              value={formik.values.name}
              onValueChange={formik.handleChange('name')}
              error={formik.touched.name && formik.errors.name}
              testID="nameInput"
            />
            <TextInput
              placeholder="Your Telephone number"
              name="number"
              value={formik.values.number}
              onValueChange={formik.handleChange('number')}
              error={formik.touched.number && formik.errors.number}
              testID="numberInput"
            />
            <TextInput
              placeholder="Your Email Address"
              name="email"
              value={formik.values.email}
              onValueChange={formik.handleChange('email')}
              error={formik.touched.email && formik.errors.email}
              testID="emailInput"
            />
            {type === 'agentForm' && (
              <TextInput
                placeholder="Company Name"
                name="companyName"
                value={formik.values.companyName}
                onValueChange={formik.handleChange('companyName')}
                error={formik.touched.companyName && formik.errors.companyName}
                testID="companyNameInput"
              />
            )}
            <TextArea
              placeholder="Comments"
              name="comments"
              value={formik.values.comments}
              onValueChange={formik.handleChange('comments')}
              rows={5}
              testID="commentsInput"
              error={formik.touched.comments && formik.errors.comments}
            />
            <Button type="submit" loading={sending} testID="submitButton">
              Submit
            </Button>
          </FormContainer>
        </form>
      )}
    </FormWrapper>
  );
};

export default FaqForm;
